/**
 * F38. KPIs financieros con semáforo.
 *
 * Conjunto de indicadores económico-financieros agregados a partir del libro
 * diario contable. Se calculan:
 *
 *   - Ratios de balance: liquidez corriente, prueba ácida, fondo de maniobra,
 *     endeudamiento, calidad de la deuda.
 *   - Ratios de P&L: margen bruto %, margen EBITDA %, margen neto %,
 *     crecimiento de ventas MoM (mes/mes) y YoY (año/año).
 *
 * Cada ratio incluye un estado de semáforo (verde / amarillo / rojo / n.a.)
 * según umbrales definidos en el documento de KPIs. Si no hay denominador
 * (división por 0), value=null y status='na'.
 *
 * Acompañan al resultado:
 *   - Variables intermedias (AC, ANC, PC, PNC, PN, EX, ING, CMV, GP, GOP, DA,
 *     GFI, IMP, márgenes) en céntimos, para que el front no tenga que
 *     recalcularlas.
 *   - Drilldown por bloque PGC con las cuentas que componen cada variable.
 *   - Waterfall pre-computado (ING → CMV → GP → GOP → DA → GFI → IMP → BN)
 *     para gráfico de cascada del P&L.
 *   - Vertical analysis (cada gasto/subtotal como % de ING).
 *
 * Solo accesible para usuarios con rol ADMIN.
 */
import { z } from 'zod';
// ============================================================================
// Estado semáforo y ratios
// ============================================================================
export const SemaforoSchema = z.enum(['green', 'yellow', 'red', 'na']);
export const RatioSchema = z.object({
    /** Valor del ratio. null si división por 0 (N/A). */
    value: z.number().nullable(),
    status: SemaforoSchema,
    /** Fórmula matemática (p.ej. "AC / PC"). */
    formula: z.string(),
    /** Nombre legible del ratio. */
    label: z.string(),
    /** Explicación breve del significado. */
    tooltip: z.string(),
    /** Descripción humana de los umbrales (p.ej. "Verde: 1.5–2.2"). */
    thresholds: z.string(),
});
// ============================================================================
// Variables intermedias (céntimos)
// ============================================================================
export const BalanceVarsSchema = z.object({
    /** Activo Corriente. */
    AC: z.number(),
    /** Activo No Corriente. */
    ANC: z.number(),
    /** Pasivo Corriente. */
    PC: z.number(),
    /** Pasivo No Corriente. */
    PNC: z.number(),
    /** Patrimonio Neto (incluye resultado del ejercicio dinámico). */
    PN: z.number(),
    /** Existencias (subconjunto de AC). */
    EX: z.number(),
    /** Activo Total = AC + ANC. */
    AT: z.number(),
    /** Pasivo Total = PC + PNC. */
    PT: z.number(),
});
export const PnlVarsSchema = z.object({
    /** Ingresos (cuenta 70). */
    ING: z.number(),
    /** Coste de mercancías vendidas / aprovisionamientos (60). */
    CMV: z.number(),
    /** Gastos de personal (64). */
    GP: z.number(),
    /** Gastos operativos (62 + 63 sin IS). */
    GOP: z.number(),
    /** Dotación a amortización (68). */
    DA: z.number(),
    /** Gastos financieros (66). */
    GFI: z.number(),
    /** Impuesto sobre beneficios (630 / 6300). */
    IMP: z.number(),
    /** Margen bruto = ING - CMV. */
    margenBruto: z.number(),
    /** EBITDA = margen bruto - GP - GOP. */
    ebitda: z.number(),
    /** EBIT = EBITDA - DA. */
    ebit: z.number(),
    /** Beneficio neto = EBIT - GFI - IMP. */
    beneficioNeto: z.number(),
});
// ============================================================================
// Drilldown y análisis vertical
// ============================================================================
export const AccountRowSchema = z.object({
    code: z.string(),
    name: z.string(),
    /** Saldo en céntimos (signo natural según tipo de cuenta). */
    amount: z.number(),
});
export const VerticalAnalysisRowSchema = z.object({
    label: z.string(),
    amount: z.number(),
    /** Porcentaje sobre ING (0-100). null si ING=0. */
    pctOfIng: z.number().nullable(),
});
export const WaterfallStepSchema = z.object({
    label: z.string(),
    type: z.enum(['start', 'positive', 'negative', 'subtotal', 'end']),
    /** Valor del paso (negativo para gastos que restan). */
    value: z.number(),
    /** Saldo acumulado tras el paso. */
    cumulative: z.number(),
});
// ============================================================================
// Respuesta principal
// ============================================================================
export const FinancialKpisResponseSchema = z.object({
    period: z.object({
        from: z.string(),
        to: z.string(),
        label: z.string(),
    }),
    balance: z.object({
        asOf: z.string(),
        vars: BalanceVarsSchema,
        ratios: z.object({
            liquidezCorriente: RatioSchema,
            pruebaAcida: RatioSchema,
            fondoManiobra: RatioSchema,
            endeudamiento: RatioSchema,
            calidadDeuda: RatioSchema,
        }),
        /** Cuentas que componen cada variable. Claves: AC, ANC, PC, PNC, PN, EX. */
        drilldown: z.record(z.array(AccountRowSchema)),
    }),
    pnl: z.object({
        vars: PnlVarsSchema,
        ratios: z.object({
            margenBrutoPct: RatioSchema,
            ebitdaMarginPct: RatioSchema,
            netMarginPct: RatioSchema,
            salesGrowthMoM: RatioSchema,
            salesGrowthYoY: RatioSchema,
        }),
        verticalAnalysis: z.array(VerticalAnalysisRowSchema),
        waterfall: z.array(WaterfallStepSchema),
        /** Cuentas que componen cada variable. Claves: ING, CMV, GP, GOP, DA, GFI, IMP. */
        drilldown: z.record(z.array(AccountRowSchema)),
    }),
    previousPeriod: z
        .object({
        label: z.string(),
        ING: z.number(),
    })
        .nullable(),
    previousYear: z
        .object({
        label: z.string(),
        ING: z.number(),
    })
        .nullable(),
});
// ============================================================================
// Query
// ============================================================================
export const FinancialKpisQuerySchema = z
    .object({
    year: z.coerce.number().int().min(2000).max(2100),
    period: z.enum(['YEAR', 'Q1', 'Q2', 'Q3', 'Q4', 'MONTH']).default('YEAR'),
    month: z.coerce.number().int().min(1).max(12).optional(),
})
    .refine((q) => q.period !== 'MONTH' || typeof q.month === 'number', {
    message: 'month es obligatorio cuando period=MONTH',
    path: ['month'],
});
//# sourceMappingURL=financial-kpis.js.map